import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, FlexColumn, Text } from 'core/components';
import { AGENT_TURN_STATUS_TYPES, TOOL_CALL_STATUS, TOOL_DECISION_REFUSAL } from '@kentik/ui-shared/kaizen/constants';
import { dateTimeFormatter } from 'core/util/dateUtils';
import { MCP_BUILTIN_SERVER_KEY } from '@kentik/ui-shared/kaizen/mcpBuiltinServers';
import { OAuthControls } from 'app/components/McpOAuthControls';
import { InfobloxLogo } from 'app/components/InfobloxLogo';
import { CONNECT_CARD_STATE, connectCardState } from './util/connectCardState';
import { REFUSAL_TEXT } from './util/decisionRefusal';

const stopPropagation = (e) => e.stopPropagation();

const KaizenToolResultConnectActions = ({
  $mcpServers,
  $builtinMcpServers,
  tool,
  turn,
  sessionId,
  isAsker,
  askerName,
  onContinue,
  onSkip,
  onCancel,
  disabled
}) => {
  const { server_id: serverId, server_name: serverName, builtin_key: builtinKey } = tool.pause_context;
  const isWaiting = tool.status === TOOL_CALL_STATUS.AWAITING_APPROVAL;
  const refusedAsNotConnected = tool.decision_refused === TOOL_DECISION_REFUSAL.NOT_CONNECTED;

  useEffect(() => {
    if (!isAsker || !isWaiting) {
      return;
    }

    if (builtinKey) {
      $builtinMcpServers.load(builtinKey);
    } else {
      $mcpServers.fetchOAuthStatus(serverId);
    }
  }, [$mcpServers, $builtinMcpServers, builtinKey, isAsker, isWaiting, serverId, refusedAsNotConnected]);

  const oauthStatus = builtinKey
    ? $builtinMcpServers.getOAuthStatus(builtinKey)
    : $mcpServers.oauthStatusMap.get(serverId);
  const { state, expiresAt } = connectCardState({ tool, turn, oauthStatus, isAsker });

  if (state === CONNECT_CARD_STATE.DONE) {
    return null;
  }

  const pause = { session_id: sessionId, agent_step_id: tool.agent_step_id, tool_tracking_id: tool.tool_tracking_id };
  const handleAuthorize = () =>
    builtinKey
      ? $builtinMcpServers.connect(builtinKey, { pause })
      : $mcpServers.connectOAuth(serverId, { serverName, pause });

  const skipAndCancel = (
    <>
      <Button small onClick={onSkip} disabled={disabled}>
        Skip
      </Button>
      <Button small onClick={onCancel} disabled={disabled || !onCancel}>
        Cancel
      </Button>
    </>
  );

  const renderState = () => {
    switch (state) {
      case CONNECT_CARD_STATE.AUTHORIZE:
      case CONNECT_CARD_STATE.CONNECTING:
        return (
          <>
            <Text small>AI Advisor needs access to {serverName} to continue.</Text>
            {tool.query_object?.prompt && (
              <Text small muted>
                {tool.query_object.prompt}
              </Text>
            )}
            <Flex gap={1} alignItems="flex-start" flexWrap="wrap">
              <OAuthControls
                status={oauthStatus}
                connectText={`Authorize ${serverName}`}
                onConnect={handleAuthorize}
                icon={builtinKey === MCP_BUILTIN_SERVER_KEY.INFOBLOX_IQ ? <InfobloxLogo size={14} /> : undefined}
                intent={builtinKey ? 'primary' : undefined}
                loading={oauthStatus?.connecting && (!builtinKey || !oauthStatus.authorizeUrl)}
              />
              {skipAndCancel}
            </Flex>
            <Text small muted>
              {expiresAt
                ? `Waiting for you. Expires at ${dateTimeFormatter({ value: expiresAt })}.`
                : 'Waiting for you.'}
            </Text>
          </>
        );
      case CONNECT_CARD_STATE.CONTINUE:
        return (
          <>
            <Text small>{serverName} is connected.</Text>
            <Flex gap={1} flexWrap="wrap">
              <Button small intent="primary" onClick={onContinue} disabled={disabled}>
                Continue
              </Button>
              {skipAndCancel}
            </Flex>
          </>
        );
      case CONNECT_CARD_STATE.CONTINUING:
        return (
          <Text small muted>
            {turn?.processing_status === AGENT_TURN_STATUS_TYPES.AWAITING_APPROVAL
              ? `${serverName} is connected.`
              : 'Connected. Continuing…'}
          </Text>
        );
      case CONNECT_CARD_STATE.WAITING_FOR_ASKER:
        return (
          <>
            <Text small muted>
              Waiting for {askerName || 'the person who asked'} to connect {serverName}.
            </Text>
            <Flex gap={1}>
              <Button small onClick={onContinue} disabled={disabled}>
                Continue
              </Button>
              {skipAndCancel}
            </Flex>
          </>
        );
      case CONNECT_CARD_STATE.SKIPPED:
        return (
          <Text small muted>
            Skipped {serverName}.
          </Text>
        );
      case CONNECT_CARD_STATE.STOPPED:
        return (
          <Text small muted>
            Canceled.
          </Text>
        );
      default:
        return (
          <Text small muted>
            Expired.
          </Text>
        );
    }
  };

  const refusalText = isWaiting && REFUSAL_TEXT[tool.decision_refused]?.(serverName);

  return (
    <Box mt={1} onClick={stopPropagation}>
      <FlexColumn gap={1} alignItems="flex-start">
        {renderState()}
        {refusalText && (
          <Text small color="warning">
            {refusalText}
          </Text>
        )}
      </FlexColumn>
    </Box>
  );
};

export default inject('$mcpServers', '$builtinMcpServers')(observer(KaizenToolResultConnectActions));
